import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Sparkles, Loader2, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-crafted Oliver answers for preset questions
const PRESET_ANSWERS = {
  "How much could I claim?": `That's the question everyone wants answered and it's a fair one. The FCA has indicated that the average compensation per agreement could be in the region of £830, and with the total industry redress estimated at £7.5 billion, the scale of mis-selling was enormous.

What that means in practice is that your figure could be higher or lower depending on how many agreements you had, which lenders were involved, and what commission was charged. The commission wasn't a fixed amount — dealers had discretion to set your interest rate within a range, and the higher they set it, the more they earned. Some people were put on significantly higher rates than they needed to be, and that difference compounds over the life of a finance agreement into a meaningful sum.

This is also where having a specialist solicitor genuinely matters. Unlike claims management companies, BlueLion Law are solicitors who specialise in litigation and understand quantum — which is the legal assessment of exactly what you're owed. That means we don't just submit your claim and accept whatever the lender offers back. We scrutinise every settlement to make sure it's correct, and if a lender tries to undervalue or reject your claim, we have the legal expertise to challenge them. You're not just getting someone to fill in a form — you're getting a law firm in your corner.

The only way to know your number is to let us check — it takes a few minutes, no lender paperwork needed, and won't affect your credit score.`,

  "Do I need my paperwork?": `This is something that stops a lot of people from even starting and I completely understand why — most of us don't keep finance documents from years ago, and even if we did, who knows which drawer they ended up in.

The good news is you don't need any of your original finance paperwork. Our system can locate your finance agreements going all the way back to 2007 using nothing more than your name, date of birth and current address. It doesn't matter if you've moved house multiple times, changed your name, or have absolutely no memory of the lender's name or the amount you borrowed. We have access to the tools to find it all.

What's important to understand is that the agreements you've forgotten about are often the ones that turn out to be the most valuable. People who had multiple cars on finance over the years frequently discover claims they had no idea existed. The paperwork was never your responsibility to keep — the lenders have records of everything, and we know exactly how to access them on your behalf.

All you need to do is start. We handle everything else from there.`,

  "Will it affect my credit score?": `It's a really sensible question and I'm glad you asked it — too many people assume the worst and talk themselves out of checking when there's genuinely nothing to worry about.

The search we run is a soft check only. That means it's completely invisible to lenders, leaves no footprint on your credit file whatsoever, and has absolutely zero impact on your credit score. It works entirely differently to a credit application or a loan search, which leave a hard footprint that other lenders can see. This leaves none.

Your credit score will be exactly the same after we check as it was before. The only thing that can change is that you might discover you're owed compensation you didn't know about. There is genuinely no downside to finding out — and the cost of not checking could be far greater than you realise.

You can start with complete peace of mind.`,

  "Can I make multiple claims?": `You absolutely can, and this is the part that genuinely surprises most people when they first hear it. Every single car finance agreement you've ever had is treated as a completely separate claim in its own right — each one is assessed individually, each one could carry its own compensation entitlement, and each lender is dealt with separately.

So if you've had two, three, four or more vehicles on finance since 2007, you could have multiple claims running simultaneously. Most people instinctively think about their most recent car, but the older agreements are frequently the most valuable. The commission practices in the earlier years were even less regulated and the interest rate manipulation was more widespread, which means the overcharging was often more significant.

There's also something else worth knowing. Many people financed cars for family members — a spouse, a child learning to drive, a partner. If your name was on that agreement, it may well be claimable too. The scope is often much broader than people initially assume.

We always recommend letting us check your full history comprehensively rather than assuming you only have one claim. The difference between checking one agreement and checking all of them could be the difference between a modest sum and something genuinely life-changing.`,

  "What is mis-sold finance?": `Mis-selling in car finance happened on a truly enormous scale and the vast majority of people affected had absolutely no idea it was happening to them at the time — which is precisely what made it so widespread.

Here's what was going on. When you arranged finance through a car dealership, the dealer was earning a commission from the lender for placing your business with them. So far that's not unusual — but the problem was that many dealers had something called discretionary commission arrangements, which gave them the power to set your interest rate themselves within a range set by the lender. The higher the rate they put you on, the bigger their commission. There was a direct financial incentive to charge you as much as possible, and you were never told this was happening.

The FCA investigated and found this created a fundamental conflict of interest that infected millions of agreements written between 2007 and 2021. The dealer sitting across the desk from you, who appeared to be helping you find the best deal, was in many cases doing the opposite. The regulator found it so serious that they launched a full industry review and the redress process that followed is one of the largest consumer compensation exercises in UK financial history.

If you had a car on finance during that period and it was arranged through a dealership, there is a very real chance you were affected. The claim isn't about whether you struggled to make payments or whether you felt something was wrong at the time — most people didn't. It's about whether the system worked against your interests without your knowledge. For millions of people, it did.`,

  "Why use a solicitor rather than a CMC?": `It's a really important question and the difference is more significant than most people realise. A claims management company can submit a claim on your behalf — but that's largely where their power ends. If a lender rejects your claim or offers a settlement that's lower than you're entitled to, a CMC has very limited options to push back.

BlueLion Law are solicitors, which changes everything. We specialise in litigation and we understand quantum — the legal process of calculating exactly what you're owed, not just what a lender is willing to offer. That means we can challenge lenders formally, escalate to the Financial Ombudsman, and if necessary take legal action to recover what's rightfully yours. We don't accept the first number a lender puts on the table if we believe it's wrong.

There's another dimension to this that people don't always consider. Lenders know the difference between a letter from a CMC and a letter from a law firm. The legal weight behind a solicitor's correspondence is different, the obligations on the lender to respond properly are different, and the implied consequences of not settling fairly are different. That dynamic matters when you're dealing with large financial institutions whose default position is to minimise what they pay out.

In short — a CMC files a form. We fight your corner.`
};

const SmartFAQSearch = () => {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [intent, setIntent] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);  // Track if text is still streaming
  const [hasSearched, setHasSearched] = useState(false);
  
  // Ref for input focus behavior
  const inputRef = useRef(null);

  const suggestedQuestions = [
    "How much could I claim?",
    "Do I need my paperwork?",
    "Will it affect my credit score?",
    "Can I make multiple claims?",
    "What is mis-sold finance?",
    "Why use a solicitor rather than a CMC?"
  ];

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setIsStreaming(false);
    setHasSearched(true);
    setAnswer(''); // Start with empty string for streaming
    setIntent(null);

    try {
      // Use streaming endpoint for faster perceived response time
      const response = await fetch(`${BACKEND_URL}/api/rag/query/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedAnswer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'sources') {
                setIntent(data.intent);
                // Sources received - text streaming is about to start
                setIsSearching(false);
                setIsStreaming(true);
              } else if (data.type === 'text') {
                streamedAnswer += data.content;
                setAnswer(streamedAnswer);
              } else if (data.type === 'error') {
                setAnswer(data.content || "I apologise, but I'm having trouble finding that information right now.");
                setIsSearching(false);
                setIsStreaming(false);
              } else if (data.type === 'done') {
                // Streaming complete
                setIsStreaming(false);
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
      // Ensure streaming is marked complete after loop ends
      setIsStreaming(false);
    } catch (error) {
      // Log error for debugging (consider replacing with proper error logging service in production)
      if (process.env.NODE_ENV === 'development') {
        console.error('Error searching:', error);
      }
      setAnswer("I apologise, but I'm having trouble finding that information right now. Please try again or use our contact form for assistance.");
      setIsSearching(false);
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    // Check if this is a preset question with a pre-crafted answer
    if (PRESET_ANSWERS[suggestion]) {
      setIsSearching(true);
      setHasSearched(true);
      setAnswer(null);
      // Simulate a brief loading state for UX consistency
      setTimeout(() => {
        setAnswer(PRESET_ANSWERS[suggestion]);
        setIntent('high');
        setIsSearching(false);
      }, 500);
    } else {
      handleSearch(suggestion);
    }
  };

  // Handle "Ask Another Question" - scroll to section top, clear, and focus
  const handleAskAnother = () => {
    // Clear state first
    setAnswer(null);
    setHasSearched(false);
    setQuery('');
    setIntent(null);
    setIsStreaming(false);
    
    // Scroll to the parent section container (id="ask-ai") with offset for fixed nav
    const sectionElement = document.getElementById('ask-ai');
    if (sectionElement) {
      const navOffset = 80; // Offset for fixed navigation bar
      const elementPosition = sectionElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    
    // Focus and clear the input after scroll completes
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    }, 600);
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Search Input */}
      <div className="relative mb-6">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
        </div>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your question here..."
          className="pl-12 pr-24 h-14 text-base rounded-xl border-2 border-border focus:border-accent shadow-md"
        />
        <Button
          onClick={() => handleSearch()}
          disabled={isSearching || !query.trim()}
          variant="gold"
          className="absolute right-2 top-1/2 -translate-y-1/2"
        >
          {isSearching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Search
            </>
          )}
        </Button>
      </div>

      {/* Suggested Questions */}
      {!hasSearched && (
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-3 text-center">
            Popular questions:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(question)}
                className={cn(
                  'px-4 py-2 text-sm rounded-full',
                  'bg-muted hover:bg-accent/10 hover:text-accent',
                  'border border-border hover:border-accent/30',
                  'transition-all duration-200'
                )}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Answer */}
      {hasSearched && (
        <Card className={cn(
          'border-border shadow-md overflow-hidden',
          'animate-fade-in-up'
        )}>
          <CardContent className="p-6">
            {isSearching && !answer ? (
              <div className="flex items-center justify-center gap-3 py-8">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
                <span className="text-muted-foreground">Finding the best answer...</span>
              </div>
            ) : answer ? (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-gold" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      AI Answer
                      {isStreaming && <Loader2 className="w-3 h-3 animate-spin inline ml-2" />}
                    </p>
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                      {answer}
                      {isStreaming && <span className="inline-block w-2 h-4 bg-accent/50 animate-pulse ml-0.5" />}
                    </p>
                  </div>
                </div>

                {/* CTA - only show when streaming is complete */}
                {!isStreaming && !isSearching && (
                  <div className="mt-6 pt-4 border-t border-border flex flex-col sm:flex-row gap-3">
                    <Button variant="gold" className="flex-1" asChild>
                      <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">Find Your Agreements</a>
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleAskAnother}
                    >
                      Ask Another Question
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Powered by AI badge */}
      <p className="text-xs text-muted-foreground text-center mt-4">
        <Sparkles className="w-3 h-3 inline mr-1" />
        Powered by BlueLion Knowledge Brain
      </p>
    </div>
  );
};

export { SmartFAQSearch };
